/**
 * Ball geometry helpers — spherical panels with recessed seam grooves.
 *
 * Shared by the ball generator to construct basketballs, soccer balls,
 * baseballs, and footballs with geometrically accurate seams carved
 * into the surface (real recessed geometry, not overlay lines or
 * textures).
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre, matching PoseRef.
 *
 * Two construction strategies live here:
 *
 *  - `buildPanelPatch` — fan-triangulated spherical polygon whose
 *    boundary drops into a V-groove. Used by basketball and soccer,
 *    which are natively described as panel layouts.
 *
 *  - `applyCurveGrooves` — post-pass that displaces mesh vertices
 *    inward when they're close (in geodesic sense) to any seam
 *    curve. Used by baseball (figure-8 curve) and football (single
 *    longitudinal great circle) over a base sphere / spheroid mesh.
 */

import type { PrimitiveGeometry } from "../primitives.ts";
import {
  buildEdgesAndCornerEdges,
  triangulateTopologyForRendering,
} from "../topology.ts";
import type { PolygonTopology } from "../types.ts";
import { asLoopNormals } from "../types.ts";

// ---------------------------------------------------------------------------
// Vec3 helpers — local to this module to keep the file self-contained
// ---------------------------------------------------------------------------

export type Vec3 = [number, number, number];

function vLen(v: Vec3): number {
  return Math.hypot(v[0], v[1], v[2]);
}
function vNormalize(v: Vec3): Vec3 {
  const l = vLen(v) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}
function vDot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
function vSub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}
function vCross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function smoothstep(t: number): number {
  const x = Math.max(0, Math.min(1, t));
  return x * x * (3 - 2 * x);
}

// ---------------------------------------------------------------------------
// Flat-shaded mesh builder — collects triangles and emits a PrimitiveGeometry
// ---------------------------------------------------------------------------

/**
 * Incremental mesh builder. Positions are shared between triangles (dedupe
 * via a rounded-position key); per-face normals are computed once the mesh
 * is finalised. Per-corner UVs are emitted as a flat fallback — the ball
 * generator doesn't consume them but `PrimitiveGeometry` requires the slot.
 */
class MeshBuilder {
  positions: number[] = [];
  faces: number[][] = [];
  private vertKey = new Map<string, number>();

  addVertexUnique(x: number, y: number, z: number): number {
    const key = `${Math.round(x * 1e6)}_${Math.round(y * 1e6)}_${Math.round(z * 1e6)}`;
    const existing = this.vertKey.get(key);
    if (existing !== undefined) return existing;
    const idx = this.positions.length / 3;
    this.positions.push(x, y, z);
    this.vertKey.set(key, idx);
    return idx;
  }

  /** Add a vertex without welding — caller manages the index itself. */
  addVertexRaw(x: number, y: number, z: number): number {
    const idx = this.positions.length / 3;
    this.positions.push(x, y, z);
    return idx;
  }

  addTriangle(a: number, b: number, c: number): void {
    this.faces.push([a, b, c]);
  }

  finalise(): PrimitiveGeometry {
    return geometryFromTriangleFaces(this.positions, this.faces);
  }
}

// ---------------------------------------------------------------------------
// Panel patch — spherical polygon with boundary V-groove
// ---------------------------------------------------------------------------

/**
 * Convex spherical-polygon panel. `corners` are unit-vector directions on
 * the unit sphere, ordered CCW when viewed from outside the ball, so fan
 * triangulation from the centroid produces outward-facing triangles.
 */
export interface SphericalPanel {
  readonly corners: readonly Vec3[];
}

/**
 * Build one panel's mesh, centred on the ball origin, with radius `radius`
 * in the panel interior and a recessed V-groove along the panel boundary.
 *
 * The panel is fan-triangulated from its (normalised) centroid into
 * `corners.length` sub-triangles. Each sub-triangle is barycentrically
 * subdivided into an N×N grid, giving smooth interiors and enough vertex
 * density for the groove bevel.
 *
 * Groove carving uses a barycentric heuristic: each grid vertex knows its
 * geodesic distance from the outer (panel-boundary) edge of its
 * sub-triangle, and vertices within `grooveWidth` of that edge are
 * displaced radially inward by up to `grooveDepth` on a smoothstep ramp.
 *
 * Adjacent panels emit matching boundary vertices at the same positions
 * (radius = R - grooveDepth), so the two panels meet cleanly at the
 * groove bottom without needing global welding.
 *
 * @param panel        Spherical polygon to tessellate.
 * @param radius       Ball radius (interior surface), in metres.
 * @param grooveDepth  Maximum inward displacement at the boundary, metres.
 * @param grooveWidth  Angular half-width of the bevel ramp, radians on
 *                     the unit sphere. Visible groove width is ≈ 2× this
 *                     times `radius`.
 * @param subdivisions N — barycentric subdivisions per sub-triangle edge.
 */
export function buildPanelPatch(
  panel: SphericalPanel,
  radius: number,
  grooveDepth: number,
  grooveWidth: number,
  subdivisions: number,
): PrimitiveGeometry {
  const N = Math.max(2, subdivisions | 0);
  const corners = panel.corners;
  const n = corners.length;

  // Panel centroid, projected back to the unit sphere.
  let sx = 0,
    sy = 0,
    sz = 0;
  for (const c of corners) {
    sx += c[0];
    sy += c[1];
    sz += c[2];
  }
  const centroid = vNormalize([sx, sy, sz]);

  const mb = new MeshBuilder();

  for (let ci = 0; ci < n; ci++) {
    const A = corners[ci];
    const B = corners[(ci + 1) % n];
    const C = centroid;

    // Angle from centroid to the midpoint of edge AB — converts λ_C (0..1)
    // into a geodesic distance from the panel boundary so the bevel width
    // can be specified in radians independent of panel size.
    const midAB: Vec3 = vNormalize([A[0] + B[0], A[1] + B[1], A[2] + B[2]]);
    const cosAlpha = Math.max(-1, Math.min(1, vDot(C, midAB)));
    const alphaPanel = Math.acos(cosAlpha);

    // Barycentric grid: (i, j) with λ_C = i/N, λ_A = (N-i-j)/N, λ_B = j/N.
    // i = 0 → the AB edge (panel boundary, deepest groove point)
    // i = N → the centroid (top of dome)
    const grid: number[][] = [];
    for (let i = 0; i <= N; i++) {
      const row: number[] = [];
      for (let j = 0; j <= N - i; j++) {
        const lc = i / N;
        const la = (N - i - j) / N;
        const lb = j / N;
        const dir = vNormalize([
          C[0] * lc + A[0] * la + B[0] * lb,
          C[1] * lc + A[1] * la + B[1] * lb,
          C[2] * lc + A[2] * la + B[2] * lb,
        ]);

        const geoDist = lc * alphaPanel;
        const t = Math.min(1, geoDist / grooveWidth);
        const r = radius - grooveDepth * (1 - smoothstep(t));

        row.push(mb.addVertexUnique(dir[0] * r, dir[1] * r, dir[2] * r));
      }
      grid.push(row);
    }

    // Emit triangles. For the (i, j) parameterisation above, CCW winding
    // viewed from outside the ball is (i,j) → (i,j+1) → (i+1,j) for the
    // upright triangle and (i,j+1) → (i+1,j+1) → (i+1,j) for the inverted.
    for (let i = 0; i < N; i++) {
      for (let j = 0; j < N - i; j++) {
        const a = grid[i][j];
        const b = grid[i][j + 1];
        const c = grid[i + 1][j];
        mb.addTriangle(a, b, c);
        if (j < N - i - 1) {
          const d = grid[i + 1][j + 1];
          mb.addTriangle(b, d, c);
        }
      }
    }
  }

  return mb.finalise();
}

/**
 * Convert a shared-vertex array + triangle face list into a full
 * `PrimitiveGeometry` with flat per-face loop normals and zero UVs.
 * Mirrors the shape of `geometryFromFaces` in `props.ts` but scoped to
 * triangles so the ball generator doesn't pull that private helper.
 */
function geometryFromTriangleFaces(
  vertices: readonly number[],
  faces: readonly number[][],
): PrimitiveGeometry {
  const offsets = new Uint32Array(faces.length + 1);
  const corners: number[] = [];
  const loopNormals: number[] = [];
  const uvs: number[] = [];

  for (let fi = 0; fi < faces.length; fi++) {
    offsets[fi] = corners.length;
    const face = faces[fi];
    // Per-face flat normal via Newell's shortcut on the triangle
    const ax = vertices[face[0] * 3 + 0];
    const ay = vertices[face[0] * 3 + 1];
    const az = vertices[face[0] * 3 + 2];
    const bx = vertices[face[1] * 3 + 0];
    const by = vertices[face[1] * 3 + 1];
    const bz = vertices[face[1] * 3 + 2];
    const cx = vertices[face[2] * 3 + 0];
    const cy = vertices[face[2] * 3 + 1];
    const cz = vertices[face[2] * 3 + 2];
    const ux = bx - ax,
      uy = by - ay,
      uz = bz - az;
    const vx = cx - ax,
      vy = cy - ay,
      vz = cz - az;
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const nl = Math.hypot(nx, ny, nz) || 1;
    nx /= nl;
    ny /= nl;
    nz /= nl;

    for (let i = 0; i < face.length; i++) {
      corners.push(face[i]);
      loopNormals.push(nx, ny, nz);
      uvs.push(0, 0);
    }
  }
  offsets[faces.length] = corners.length;

  const cornersArr = new Uint32Array(corners);
  const topology: PolygonTopology = {
    offsets,
    corners: cornersArr,
    ...buildEdgesAndCornerEdges(offsets, cornersArr),
  };
  return {
    vertices: new Float32Array(vertices),
    indices: triangulateTopologyForRendering(topology),
    loopNormals: asLoopNormals(new Float32Array(loopNormals)),
    topology,
    uvs: new Float32Array(uvs),
  };
}

// ---------------------------------------------------------------------------
// UV sphere with extra ring density — base mesh for curve-groove balls
// ---------------------------------------------------------------------------

/**
 * Build a UV sphere whose tessellation is dense enough for a
 * `carveCurveGroove` post-pass to render a clean seam. Returns a mesh
 * builder's raw positions and triangle list so the caller can mutate
 * vertex radii before finalising. The sphere is seamless in the sense
 * that every vertex is shared between all adjacent triangles, so radial
 * displacement propagates correctly without welding artefacts.
 */
export function buildSphereMeshRaw(
  radius: number,
  segments: number,
  rings: number,
): { positions: number[]; faces: number[][] } {
  const positions: number[] = [];
  const faces: number[][] = [];

  // North pole
  const northIdx = 0;
  positions.push(0, 0, radius);
  // Interior rings
  const ringFirstIdx: number[] = [];
  for (let r = 1; r < rings; r++) {
    const phi = (Math.PI * r) / rings;
    const sinP = Math.sin(phi);
    const cosP = Math.cos(phi);
    ringFirstIdx.push(positions.length / 3);
    for (let s = 0; s < segments; s++) {
      const theta = (2 * Math.PI * s) / segments;
      positions.push(
        radius * sinP * Math.cos(theta),
        radius * sinP * Math.sin(theta),
        radius * cosP,
      );
    }
  }
  // South pole
  const southIdx = positions.length / 3;
  positions.push(0, 0, -radius);

  // North cap triangles
  const firstRing = ringFirstIdx[0];
  for (let s = 0; s < segments; s++) {
    const a = firstRing + s;
    const b = firstRing + ((s + 1) % segments);
    faces.push([northIdx, a, b]);
  }
  // Interior quads (as two triangles each)
  for (let r = 0; r < rings - 2; r++) {
    const top = ringFirstIdx[r];
    const bot = ringFirstIdx[r + 1];
    for (let s = 0; s < segments; s++) {
      const s1 = (s + 1) % segments;
      const a = top + s;
      const b = top + s1;
      const c = bot + s1;
      const d = bot + s;
      faces.push([a, b, c]);
      faces.push([a, c, d]);
    }
  }
  // South cap triangles
  const lastRing = ringFirstIdx[ringFirstIdx.length - 1];
  for (let s = 0; s < segments; s++) {
    const a = lastRing + ((s + 1) % segments);
    const b = lastRing + s;
    faces.push([southIdx, a, b]);
  }

  return { positions, faces };
}

/**
 * Build a prolate-spheroid mesh by revolving a (z, r) profile around Z.
 * Every profile row becomes one ring of `segments` vertices; the first
 * and last rows may be single-point tips if their radius is zero. The
 * returned raw positions are safe for in-place radial mutation.
 */
export function buildRevolvedMeshRaw(
  profile: readonly [number, number][],
  segments: number,
): { positions: number[]; faces: number[][] } {
  const positions: number[] = [];
  const faces: number[][] = [];
  const rows: number[][] = [];

  for (const [z, r] of profile) {
    if (r <= 1e-6) {
      const idx = positions.length / 3;
      positions.push(0, 0, z);
      rows.push([idx]);
      continue;
    }
    const row: number[] = [];
    for (let s = 0; s < segments; s++) {
      const theta = (2 * Math.PI * s) / segments;
      const idx = positions.length / 3;
      positions.push(r * Math.cos(theta), r * Math.sin(theta), z);
      row.push(idx);
    }
    rows.push(row);
  }

  for (let i = 0; i < rows.length - 1; i++) {
    const top = rows[i];
    const bot = rows[i + 1];
    if (top.length === 1 && bot.length === segments) {
      for (let s = 0; s < segments; s++) {
        faces.push([top[0], bot[s], bot[(s + 1) % segments]]);
      }
    } else if (top.length === segments && bot.length === 1) {
      for (let s = 0; s < segments; s++) {
        faces.push([top[s], top[(s + 1) % segments], bot[0]]);
      }
    } else if (top.length === segments && bot.length === segments) {
      for (let s = 0; s < segments; s++) {
        const s1 = (s + 1) % segments;
        faces.push([top[s], top[s1], bot[s1]]);
        faces.push([top[s], bot[s1], bot[s]]);
      }
    }
  }

  return { positions, faces };
}

// ---------------------------------------------------------------------------
// Curve groove carving — post-pass for baseball / football seams
// ---------------------------------------------------------------------------

/**
 * Carve a set of seam curves into a raw mesh by pushing vertices that
 * lie near any curve radially inward, on a smoothstep ramp sized by
 * `grooveWidth`.
 *
 * Each curve is a list of points in 3D space (world space, i.e. already
 * scaled to the ball radius). For each mesh vertex, we find the nearest
 * curve sample via brute force (good enough at typical ball tessellation
 * counts), then displace the vertex inward along its own outward
 * direction by up to `grooveDepth`.
 *
 * The outward direction at a vertex `p` is its unit vector from the
 * ball centre `centre`. For a sphere this is the surface normal; for a
 * prolate spheroid it's close enough that narrow grooves read cleanly.
 *
 * @param positions   Flat xyz array, mutated in place.
 * @param centre      Ball centre to project radial direction from.
 * @param curves      One or more seam-curve samples, in world space.
 * @param grooveDepth Maximum inward displacement, metres.
 * @param grooveWidth Cartesian distance at which the ramp reaches 0.
 */
export function carveCurveGrooves(
  positions: number[],
  centre: Vec3,
  curves: readonly (readonly Vec3[])[],
  grooveDepth: number,
  grooveWidth: number,
): void {
  const widthSq = grooveWidth * grooveWidth;
  for (let i = 0; i < positions.length; i += 3) {
    const px = positions[i];
    const py = positions[i + 1];
    const pz = positions[i + 2];
    let bestSq = Infinity;
    for (const curve of curves) {
      for (const cp of curve) {
        const dx = px - cp[0];
        const dy = py - cp[1];
        const dz = pz - cp[2];
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 < bestSq) bestSq = d2;
      }
    }
    if (bestSq >= widthSq) continue;
    const d = Math.sqrt(bestSq);
    const t = d / grooveWidth;
    const factor = 1 - smoothstep(t);
    const displacement = grooveDepth * factor;
    // Radial inward direction from the ball centre.
    const ox = px - centre[0];
    const oy = py - centre[1];
    const oz = pz - centre[2];
    const ol = Math.hypot(ox, oy, oz) || 1;
    positions[i] = px - (ox / ol) * displacement;
    positions[i + 1] = py - (oy / ol) * displacement;
    positions[i + 2] = pz - (oz / ol) * displacement;
  }
}

/** Finalise raw positions + triangle list into a `PrimitiveGeometry`. */
export function finaliseRawMesh(
  positions: readonly number[],
  faces: readonly number[][],
): PrimitiveGeometry {
  return geometryFromTriangleFaces(positions, faces);
}

// ---------------------------------------------------------------------------
// Panel layouts — basketball octants and truncated-icosahedron soccer ball
// ---------------------------------------------------------------------------

/**
 * Basketball panel layout — three mutually perpendicular great circles
 * (in the XY, YZ, ZX planes) divide the unit sphere into eight equal
 * spherical triangles, matching the classic 8-panel grooves on a
 * Wilson/Spalding basketball. Each panel has three corners at ±X, ±Y,
 * or ±Z unit vectors, one per octant of 3-space.
 */
export function basketballPanels(): SphericalPanel[] {
  const panels: SphericalPanel[] = [];
  for (const sx of [1, -1]) {
    for (const sy of [1, -1]) {
      for (const sz of [1, -1]) {
        // Order corners CCW when viewed from outside the octant so fan
        // triangulation produces outward-facing triangles. The parity of
        // (sx * sy * sz) flips the handedness of the natural (X, Y, Z)
        // ordering, so swap two corners in half the octants to compensate.
        const x: Vec3 = [sx, 0, 0];
        const y: Vec3 = [0, sy, 0];
        const z: Vec3 = [0, 0, sz];
        const parity = sx * sy * sz;
        if (parity > 0) {
          panels.push({ corners: [x, y, z] });
        } else {
          panels.push({ corners: [x, z, y] });
        }
      }
    }
  }
  return panels;
}

/**
 * Truncated-icosahedron panel layout — the classic 32-panel soccer ball
 * (12 pentagons + 20 hexagons). Vertices are computed from the icosahedron
 * using the golden ratio, then the truncated-icosahedron vertex set is
 * generated by trisecting each original icosahedron edge. All 60 vertices
 * lie on the unit sphere after normalisation.
 */
export function soccerBallPanels(): SphericalPanel[] {
  const phi = (1 + Math.sqrt(5)) / 2;

  // 12 icosahedron vertices (three golden rectangles on the coordinate planes).
  const icoVerts: Vec3[] = [];
  for (const a of [-1, 1]) {
    for (const b of [-phi, phi]) {
      icoVerts.push([0, a, b]);
      icoVerts.push([a, b, 0]);
      icoVerts.push([b, 0, a]);
    }
  }
  for (const v of icoVerts) {
    const l = vLen(v);
    v[0] /= l;
    v[1] /= l;
    v[2] /= l;
  }

  // 20 icosahedron faces — every triple of verts whose mutual distances
  // equal the icosahedron edge length. Compute edge length from any two
  // closest vertices, then collect all triples that match (±eps).
  let edgeLen = Infinity;
  for (let i = 0; i < icoVerts.length; i++) {
    for (let j = i + 1; j < icoVerts.length; j++) {
      const d = vLen(vSub(icoVerts[i], icoVerts[j]));
      if (d < edgeLen) edgeLen = d;
    }
  }
  const EPS = 1e-4;
  const icoFaces: [number, number, number][] = [];
  const isEdge = (i: number, j: number) =>
    Math.abs(vLen(vSub(icoVerts[i], icoVerts[j])) - edgeLen) < EPS;
  for (let i = 0; i < icoVerts.length; i++) {
    for (let j = i + 1; j < icoVerts.length; j++) {
      if (!isEdge(i, j)) continue;
      for (let k = j + 1; k < icoVerts.length; k++) {
        if (isEdge(i, k) && isEdge(j, k)) {
          icoFaces.push([i, j, k]);
        }
      }
    }
  }

  // Build truncation vertices: each icosahedron edge contributes two cut
  // points, one at 1/3 and one at 2/3 along its length. We key them by
  // "i,j,third" (third ∈ {1, 2}) so the pentagon and hexagon builders
  // can share the same vertex.
  const truncVerts: Vec3[] = [];
  const edgeKey = (i: number, j: number, third: 1 | 2) => {
    // Canonicalise: edge direction is from the lower-index vertex to the
    // higher-index vertex, and `third` flips with the direction.
    if (i <= j) return `${i}_${j}_${third}`;
    return `${j}_${i}_${third === 1 ? 2 : 1}`;
  };
  const truncIndex = new Map<string, number>();
  const getTruncVertex = (i: number, j: number, third: 1 | 2): number => {
    const key = edgeKey(i, j, third);
    const existing = truncIndex.get(key);
    if (existing !== undefined) return existing;
    const a = i < j ? i : j;
    const b = i < j ? j : i;
    const t = i < j ? third / 3 : (3 - third) / 3;
    const p: Vec3 = [
      icoVerts[a][0] * (1 - t) + icoVerts[b][0] * t,
      icoVerts[a][1] * (1 - t) + icoVerts[b][1] * t,
      icoVerts[a][2] * (1 - t) + icoVerts[b][2] * t,
    ];
    const n = vNormalize(p);
    const idx = truncVerts.length;
    truncVerts.push(n);
    truncIndex.set(key, idx);
    return idx;
  };

  // Hexagonal panels — one per icosahedron face. The six corners come
  // from the three edges of the face, two cut points each, ordered
  // around the face rim so fan triangulation winds CCW from outside.
  const hexPanels: SphericalPanel[] = [];
  for (const [a, b, c] of icoFaces) {
    // Compute the face outward normal to check winding.
    const fn = vCross(
      vSub(icoVerts[b], icoVerts[a]),
      vSub(icoVerts[c], icoVerts[a]),
    );
    const centre: Vec3 = [
      (icoVerts[a][0] + icoVerts[b][0] + icoVerts[c][0]) / 3,
      (icoVerts[a][1] + icoVerts[b][1] + icoVerts[c][1]) / 3,
      (icoVerts[a][2] + icoVerts[b][2] + icoVerts[c][2]) / 3,
    ];
    const outward = vDot(fn, centre) > 0;
    const [p, q, r] = outward ? [a, b, c] : [a, c, b];
    // Walk rim: on edge p→q we want the 1/3 and 2/3 cuts, closest to p first.
    const corners: Vec3[] = [
      truncVerts[getTruncVertex(p, q, 1)],
      truncVerts[getTruncVertex(p, q, 2)],
      truncVerts[getTruncVertex(q, r, 1)],
      truncVerts[getTruncVertex(q, r, 2)],
      truncVerts[getTruncVertex(r, p, 1)],
      truncVerts[getTruncVertex(r, p, 2)],
    ];
    hexPanels.push({ corners });
  }

  // Pentagonal panels — one per icosahedron vertex. The five corners are
  // the 1/3 cut points on the five edges meeting that vertex. Collect
  // them, sort angularly around the vertex direction, and order CCW.
  const pentPanels: SphericalPanel[] = [];
  for (let vi = 0; vi < icoVerts.length; vi++) {
    // Neighbours: the five vertices connected to vi by an icosahedron edge.
    const neighbours: number[] = [];
    for (let vj = 0; vj < icoVerts.length; vj++) {
      if (vi !== vj && isEdge(vi, vj)) neighbours.push(vj);
    }
    // Build a local 2D frame in the tangent plane at vi to sort angularly.
    const axis = icoVerts[vi];
    const ref: Vec3 = Math.abs(axis[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
    const u = vNormalize(vCross(axis, ref));
    const w = vCross(axis, u);
    const withAngle: { idx: number; angle: number }[] = neighbours.map((vj) => {
      const cut = truncVerts[getTruncVertex(vi, vj, 1)];
      const du = vDot(cut, u);
      const dw = vDot(cut, w);
      return { idx: getTruncVertex(vi, vj, 1), angle: Math.atan2(dw, du) };
    });
    withAngle.sort((a, b) => a.angle - b.angle);
    const corners = withAngle.map((x) => truncVerts[x.idx]);
    pentPanels.push({ corners });
  }

  return [...hexPanels, ...pentPanels];
}

// ---------------------------------------------------------------------------
// Seam curves — baseball figure-8 and football longitudinal meridian
// ---------------------------------------------------------------------------

/**
 * Classic tennis-ball / baseball seam curve on the unit sphere.
 * Parameterisation from the standard result
 *   p(t) = (a cos t + b cos 3t, a sin t − b sin 3t, 2√(ab) sin 2t)
 * which satisfies |p(t)|² = (a + b)² for all t, so a + b = 1 keeps the
 * curve exactly on the unit sphere. Different ratios tune how curved the
 * seam looks; a ≈ 0.72, b ≈ 0.28 produces the classic baseball figure-8.
 *
 * Output is in world space, already scaled to `radius`.
 */
export function baseballSeamCurve(radius: number, samples: number): Vec3[] {
  const a = 0.72;
  const b = 1 - a; // keeps |p| = 1 on the unit sphere
  const k = 2 * Math.sqrt(a * b);
  const out: Vec3[] = [];
  for (let i = 0; i < samples; i++) {
    const t = (2 * Math.PI * i) / samples;
    const x = a * Math.cos(t) + b * Math.cos(3 * t);
    const y = a * Math.sin(t) - b * Math.sin(3 * t);
    const z = k * Math.sin(2 * t);
    out.push([x * radius, y * radius, z * radius]);
  }
  return out;
}

/**
 * Football seam — one longitudinal great-circle curve in the XZ plane,
 * sampled at whatever (z, r) profile the football's revolved mesh uses.
 * Points are emitted at both the +X and −X meridians so the curve is
 * one complete closed loop that wraps tip-to-tip on both sides.
 */
export function footballSeamCurve(
  profile: readonly [number, number][],
  samples: number,
): Vec3[] {
  const half = Math.max(2, Math.floor(samples / 2));
  const out: Vec3[] = [];
  // Linear interpolation along the profile by fractional index.
  const sampleProfile = (t: number): { z: number; r: number } => {
    const pos = t * (profile.length - 1);
    const i0 = Math.floor(pos);
    const i1 = Math.min(profile.length - 1, i0 + 1);
    const f = pos - i0;
    return {
      z: profile[i0][0] * (1 - f) + profile[i1][0] * f,
      r: profile[i0][1] * (1 - f) + profile[i1][1] * f,
    };
  };
  // Walk up the +X meridian from bottom tip to top tip …
  for (let i = 0; i < half; i++) {
    const { z, r } = sampleProfile(i / half);
    out.push([r, 0, z]);
  }
  // … then back down the −X meridian from top tip to bottom tip,
  // giving one closed great-circle seam in the XZ plane.
  for (let i = 0; i < half; i++) {
    const { z, r } = sampleProfile(1 - i / half);
    out.push([-r, 0, z]);
  }
  return out;
}
